import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { MainNavigation } from '../main/MainNavigation';
import { ChefHat } from 'lucide-react';

export function TermsOfService() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-menioo-cream">
      <MainNavigation />
      
      <div className="container mx-auto px-6 md:px-12 py-12">
        <div className="max-w-4xl mx-auto bg-white rounded-lg shadow-md p-8 md:p-12">
          <h1 className="text-4xl font-display font-bold text-menioo-navy mb-4">
            Conditions Générales d'Utilisation de Menioo
          </h1>
          
          <p className="text-gray-600 mb-8">
            <strong>Dernière mise à jour : Février 2026</strong>
          </p>

          <div className="prose prose-lg max-w-none">
            {/* Article 1 */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-menioo-navy mb-4">Article 1 – Objet</h2>
              <p className="text-gray-700 mb-4">
                Les présentes Conditions Générales d'Utilisation (ci-après « CGU ») ont pour objet de définir les modalités et conditions d'accès et d'utilisation de l'application mobile Menioo (ci-après « l'Application »), éditée par Menioo.
              </p>
              <p className="text-gray-700 mb-4">
                L'Application est un service communautaire de planification de repas et de consultation de recettes de cuisine permettant aux utilisateurs de découvrir, rechercher, créer, importer et partager des recettes, de planifier leurs repas, ainsi que d'accéder à des suggestions de produits alimentaires associés aux ingrédients des recettes.
              </p>
              <p className="text-gray-700">
                L'utilisation de l'Application implique l'acceptation pleine et entière des présentes CGU.
              </p>
            </section>

            {/* Article 2 */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-menioo-navy mb-4">Article 2 – Définitions</h2>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li><strong>Utilisateur</strong> : toute personne physique accédant à l'Application, qu'elle dispose ou non d'un Compte.</li>
                <li><strong>Compte</strong> : espace personnel de l'Utilisateur créé lors de son inscription sur l'Application.</li>
                <li><strong>Contenu Utilisateur</strong> : ensemble des recettes, images, commentaires, notes, avis et autres contenus publiés par les Utilisateurs sur l'Application.</li>
                <li><strong>Contenu Éditorial</strong> : contenu produit et publié directement par l'Éditeur.</li>
                <li><strong>Outil d'Import</strong> : fonctionnalité technique permettant à l'Utilisateur d'importer des recettes depuis des sources externes (sites web, TikTok, etc.).</li>
                <li><strong>Services</strong> : ensemble des fonctionnalités mises à disposition de l'Utilisateur via l'Application.</li>
              </ul>
            </section>

            {/* Article 3 */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-menioo-navy mb-4">Article 3 – Accès à l'Application</h2>
              <p className="text-gray-700 mb-4">
                L'Application est accessible gratuitement à tout Utilisateur disposant d'un accès à Internet et d'un appareil compatible. Certaines fonctionnalités avancées peuvent nécessiter un abonnement Premium.
              </p>
              <p className="text-gray-700">
                L'Éditeur se réserve le droit de suspendre ou d'interrompre l'accès à l'Application, notamment pour des raisons de maintenance, de mise à jour ou pour toute autre raison technique, sans que cela ne puisse donner lieu à une quelconque indemnisation.
              </p>
            </section>

            {/* Article 4 */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-menioo-navy mb-4">Article 4 – Inscription et Compte Utilisateur</h2>
              
              <h3 className="text-xl font-semibold text-menioo-navy mb-3">4.1 – Création de compte</h3>
              <p className="text-gray-700 mb-4">
                L'accès à certaines fonctionnalités de l'Application (publication de recettes, planification de repas, etc.) nécessite la création d'un Compte. L'Utilisateur s'engage à fournir des informations exactes et à jour lors de son inscription.
              </p>

              <h3 className="text-xl font-semibold text-menioo-navy mb-3">4.2 – Sécurité du compte</h3>
              <p className="text-gray-700 mb-4">
                L'Utilisateur est seul responsable de la confidentialité de ses identifiants de connexion. Toute utilisation de l'Application réalisée depuis son Compte est réputée avoir été effectuée par l'Utilisateur. En cas d'utilisation non autorisée, l'Utilisateur s'engage à en informer immédiatement l'Éditeur.
              </p>

              <h3 className="text-xl font-semibold text-menioo-navy mb-3">4.3 – Suppression du compte</h3>
              <p className="text-gray-700">
                L'Utilisateur peut demander la suppression de son Compte à tout moment en contactant le service client ou directement depuis les paramètres de l'Application. La suppression entraîne la suppression définitive des données personnelles conformément à notre Politique de Confidentialité.
              </p>
            </section>

            {/* Article 5 */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-menioo-navy mb-4">Article 5 – Description des Services</h2>
              <p className="text-gray-700 mb-3">L'Application propose les services suivants :</p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li>Consultation et recherche de recettes de cuisine</li>
                <li>Création et publication de recettes par les Utilisateurs</li>
                <li>Import de recettes depuis des sources externes (TikTok, sites web, etc.)</li>
                <li>Planification de repas et gestion de calendrier</li>
                <li>Génération automatique de listes de courses</li>
                <li>Notation et commentaires communautaires sur les recettes</li>
                <li>Sauvegarde de recettes favorites</li>
                <li>Gestion d'organisations (familles, groupes)</li>
              </ul>
            </section>

            {/* Article 6 */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-menioo-navy mb-4">Article 6 – Nature Communautaire de l'Application</h2>
              <p className="text-gray-700 mb-4">
                L'Application est une plateforme communautaire. Les recettes, avis, notes, commentaires et autres Contenus Utilisateur représentent des contributions personnelles de leurs auteurs respectifs. L'Éditeur ne garantit ni la qualité, ni l'exactitude, ni la fiabilité, ni l'exhaustivité des Contenus Utilisateur.
              </p>
              <p className="text-gray-700">
                Chaque Utilisateur est invité à exercer son propre jugement avant de suivre une recette ou un conseil publié par un autre Utilisateur.
              </p>
            </section>

            {/* Article 7 */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-menioo-navy mb-4">Article 7 – Outil d'Import de Recettes</h2>
              
              <h3 className="text-xl font-semibold text-menioo-navy mb-3">7.1 – Nature de l'outil</h3>
              <p className="text-gray-700 mb-4">
                L'Application met à disposition des Utilisateurs un Outil d'Import permettant d'importer des recettes depuis des sources externes (TikTok, sites web, blogs, etc.). Cet outil utilise l'intelligence artificielle pour extraire automatiquement les ingrédients et instructions.
              </p>
              <p className="text-gray-700 mb-4">
                <strong>La mise à disposition de l'Outil d'Import ne constitue en aucun cas une autorisation de reproduire, copier ou diffuser des contenus protégés par le droit d'auteur ou par tout autre droit de propriété intellectuelle.</strong>
              </p>

              <h3 className="text-xl font-semibold text-menioo-navy mb-3">7.2 – Responsabilité de l'Utilisateur</h3>
              <p className="text-gray-700 mb-3">Lorsqu'il utilise l'Outil d'Import, l'Utilisateur est seul responsable :</p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
                <li>Du respect des droits d'auteur et de propriété intellectuelle des sites sources</li>
                <li>De la vérification qu'il dispose des droits nécessaires pour importer et publier le contenu</li>
                <li>Du choix de la visibilité du contenu importé (privé ou public)</li>
              </ul>
              <p className="text-gray-700">
                L'Éditeur recommande fortement aux Utilisateurs de conserver les recettes importées en mode privé, sauf s'ils disposent de l'autorisation explicite de l'auteur original pour une diffusion publique.
              </p>
            </section>

            {/* Article 8 */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-menioo-navy mb-4">Article 8 – Propriété Intellectuelle</h2>
              <p className="text-gray-700 mb-4">
                L'ensemble des éléments composant l'Application (interface, design, logos, marques, logiciels) est protégé par les dispositions du Code de la propriété intellectuelle et appartient à l'Éditeur.
              </p>
              <p className="text-gray-700 mb-4">
                En publiant du contenu sur l'Application en mode public, l'Utilisateur accorde à l'Éditeur une licence non exclusive, gratuite, mondiale pour utiliser, reproduire et diffuser ledit contenu dans le cadre de l'exploitation de l'Application. L'Utilisateur conserve l'ensemble de ses droits de propriété intellectuelle sur ses contenus.
              </p>
            </section>

            {/* Article 9 */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-menioo-navy mb-4">Article 9 – Limitation de Responsabilité</h2>
              
              <h3 className="text-xl font-semibold text-menioo-navy mb-3">9.1 – Informations nutritionnelles et recettes</h3>
              <p className="text-gray-700 mb-4">
                Les recettes, informations nutritionnelles, dosages et conseils présents sur l'Application sont fournis à titre purement indicatif. L'Éditeur ne saurait en aucun cas être tenu responsable des erreurs, omissions ou inexactitudes dans les recettes, ni des conséquences liées à leur utilisation, notamment en cas d'allergies alimentaires, d'intolérances ou d'intoxications.
              </p>

              <h3 className="text-xl font-semibold text-menioo-navy mb-3">9.2 – Disponibilité du service</h3>
              <p className="text-gray-700 mb-4">
                L'Éditeur s'efforce d'assurer la disponibilité de l'Application mais ne garantit pas un accès ininterrompu ni exempt d'erreurs.
              </p>

              <h3 className="text-xl font-semibold text-menioo-navy mb-3">9.3 – Limitation générale</h3>
              <p className="text-gray-700">
                Dans les limites autorisées par la loi, la responsabilité de l'Éditeur ne pourra être engagée qu'en cas de faute prouvée et est limitée aux dommages directs et prévisibles, à l'exclusion de tout dommage indirect.
              </p>
            </section>

            {/* Article 10 */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-menioo-navy mb-4">Article 10 – Données Personnelles</h2>
              <p className="text-gray-700">
                Le traitement des données personnelles des Utilisateurs est régi par notre{' '}
                <Link to="/privacy" className="text-menioo-green hover:text-menioo-green-dark underline font-medium">
                  Politique de Confidentialité
                </Link>
                {' '}qui fait partie intégrante des présentes CGU.
              </p>
            </section>

            {/* Article 11 */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-menioo-navy mb-4">Article 11 – Modification des CGU</h2>
              <p className="text-gray-700">
                L'Éditeur se réserve le droit de modifier les présentes CGU à tout moment. Les Utilisateurs seront informés de toute modification substantielle par notification dans l'Application ou par email. La poursuite de l'utilisation de l'Application après la modification des CGU vaut acceptation des nouvelles conditions.
              </p>
            </section>

            {/* Article 12 */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-menioo-navy mb-4">Article 12 – Droit Applicable et Juridiction</h2>
              <p className="text-gray-700 mb-4">
                Les présentes CGU sont régies par le droit français. En cas de litige, et à défaut d'accord amiable, le litige sera porté devant les tribunaux compétents.
              </p>
              <p className="text-gray-700">
                Conformément aux dispositions du Code de la consommation, l'Utilisateur peut recourir à un médiateur de la consommation. La plateforme de résolution en ligne des litiges de la Commission européenne est accessible à l'adresse : https://ec.europa.eu/consumers/odr
              </p>
            </section>

            {/* Article 13 */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-menioo-navy mb-4">Article 13 – Contact</h2>
              <p className="text-gray-700 mb-3">
                Pour toute question relative aux présentes CGU, vous pouvez nous contacter :
              </p>
              <ul className="list-none text-gray-700 space-y-2">
                <li><strong>Par email :</strong> <a href="mailto:alex@menioo.com" className="text-menioo-green hover:underline">alex@menioo.com</a></li>
                <li><strong>Site web :</strong> <a href="https://menioo.com" className="text-menioo-green hover:underline">https://menioo.com</a></li>
              </ul>
            </section>
          </div>

          <div className="mt-12 pt-8 border-t border-gray-200">
            <Link 
              to="/" 
              className="inline-flex items-center text-menioo-green hover:text-menioo-green-dark transition-colors font-medium"
            >
              <ChefHat className="mr-2 h-5 w-5" />
              Retour à l'accueil
            </Link>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-menioo-navy text-white py-8 mt-12">
        <div className="container mx-auto px-6 md:px-12 text-center">
          <p className="text-gray-400">© {new Date().getFullYear()} Menioo. Tous droits réservés.</p>
        </div>
      </footer>
    </div>
  );
}
